/** Bike plastics and rider kit variants shown in the garage. Colours are sRGB hex. */
export interface Livery { id: string; primary: number; secondary: number }

export const BIKES: readonly Livery[] = [
  { id: 'factory', primary: 0xff6a00, secondary: 0x1a1a1a },
  { id: 'mega', primary: 0xd75706, secondary: 0xf5f5f5 },
  { id: 'blue', primary: 0x1f5bff, secondary: 0xf5f5f5 },
  { id: 'yellow', primary: 0xf2c200, secondary: 0x1a1a1a },
  { id: 'green', primary: 0x3fbf3a, secondary: 0x1a1a1a },
  { id: 'red', primary: 0xd81e2a, secondary: 0xf5f5f5 },
  // Brand liveries of the fleet (src/sim/bikes): Yamaha racing blue, Honda red, GASGAS red.
  { id: 'yamaha', primary: 0x1238b8, secondary: 0xf5f5f5 },
  { id: 'honda', primary: 0xcc0a14, secondary: 0xf5f5f5 },
  { id: 'gasgas', primary: 0xe0141e, secondary: 0x1a1a1a },
];

/**
 * Rider kit: the swatch shows primary/secondary; `kit` is the full colour set for the rider model; `art: 'school'`
 * prints the MegaMotocross jersey (black, orange-to-red halftone, back print; src/view/world/schoolKit.ts).
 */
export interface Kit extends Livery {
  kit: { jersey: number; jerseyAccent: number; pants: number; pantsAccent: number; boots: number; gloves: number; helmet: number; helmetAccent: number };
  art?: 'school';
}

export const KITS: readonly Kit[] = [
  // The school kit from the photos: black jersey with the orange-red halftone and back print, red pants with black panels.
  { id: 'school', primary: 0x111114, secondary: 0xff5a00, art: 'school',
    kit: { jersey: 0x111114, jerseyAccent: 0xf5f5f5, pants: 0xc81d26, pantsAccent: 0x121214, boots: 0x161618, gloves: 0x161618, helmet: 0xf2f2ee, helmetAccent: 0xd75706 } },
  { id: 'stealth', primary: 0x3a3a40, secondary: 0x8a8e94,
    kit: { jersey: 0x2a2a30, jerseyAccent: 0x8a8e94, pants: 0x1e1e22, pantsAccent: 0x5a5e66, boots: 0x2a2a2e, gloves: 0x2a2a30, helmet: 0x1a1a1e, helmetAccent: 0x8a8e94 } },
  { id: 'ice', primary: 0x2f7fe0, secondary: 0xf2f2f2,
    kit: { jersey: 0x2f7fe0, jerseyAccent: 0xf2f2f2, pants: 0xf2f2f2, pantsAccent: 0x2f7fe0, boots: 0xf2f2ee, gloves: 0x2f7fe0, helmet: 0xf2f2ee, helmetAccent: 0x2f7fe0 } },
  { id: 'lime', primary: 0x9bd62a, secondary: 0x14171c,
    kit: { jersey: 0x9bd62a, jerseyAccent: 0x14171c, pants: 0x14171c, pantsAccent: 0x9bd62a, boots: 0x14171c, gloves: 0x9bd62a, helmet: 0x14171c, helmetAccent: 0x9bd62a } },
  { id: 'flame', primary: 0xd81e2a, secondary: 0xf2c200,
    kit: { jersey: 0xd81e2a, jerseyAccent: 0xf2c200, pants: 0x15130d, pantsAccent: 0xd81e2a, boots: 0xf2f2ee, gloves: 0xd81e2a, helmet: 0xd81e2a, helmetAccent: 0xf2c200 } },
];

/** Helmet colourways from the school photos (shell, accent, goggle lens); the swatch shows shell/accent. */
export interface Helmet extends Livery { goggles: number }
export const HELMETS: readonly Helmet[] = [
  { id: 'airoh', primary: 0x1f4fd8, secondary: 0xd8202a, goggles: 0xf2c200 },
  { id: 'white', primary: 0xf2f2ee, secondary: 0x1a1a1e, goggles: 0x3a3020 },
  { id: 'black', primary: 0x141416, secondary: 0xff6a00, goggles: 0xd75706 },
  { id: 'whitered', primary: 0xf2f2ee, secondary: 0xd8202a, goggles: 0x3a3020 },
  { id: 'red', primary: 0xd8202a, secondary: 0xf2f2ee, goggles: 0x20242c },
];

export const helmetLivery = (id: string): Helmet => HELMETS.find((h) => h.id === id) ?? HELMETS[0]!;
export const bikeLivery = (id: string) => BIKES.find((b) => b.id === id) ?? BIKES[0]!;
export const kitLivery = (id: string): Kit => KITS.find((b) => b.id === id) ?? KITS[0]!;
export const hex = (c: number) => `#${c.toString(16).padStart(6, '0')}`;
